import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  PlayArrow,
  AddTask,
  Send,
  Undo,
  Refresh,
  InfoOutlined,
  AccountBalance,
  Receipt,
} from '@mui/icons-material';
import {
  PageHeader,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TableEmptyState,
  TypePill,
} from '../../components/common/pageKit';
import { useAuth } from '../../context/AuthContext';
import assetService, {
  DepreciationBook,
  DepreciationPreview,
  DepreciationRun,
  DepreciationLine,
  isCompaniesActLine,
} from '../../services/assetService';
import {
  fmtMoney,
  fmtDate,
  fmtNum,
  DEPRECIATION_BOOK_LABEL,
  RUN_STATUS_LABEL,
  RUN_STATUS_TONE,
  currentFinancialYear,
  financialYearDates,
  financialYearOptions,
} from './assetFormat';

const FINANCE_ROLES = ['SUPER_ADMIN', 'ADMIN', 'FINANCE'];

const currentMonthRange = (): { from: string; to: string } => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};

// ---------------------------------------------------------------------------
// Preview results table — shape depends on the book.
// ---------------------------------------------------------------------------

const PreviewTable: React.FC<{ preview: DepreciationPreview }> = ({ preview }) => {
  if (preview.lines.length === 0) {
    return (
      <TableEmptyState
        title="Nothing to depreciate for this period"
        hint={
          preview.book === 'COMPANIES_ACT'
            ? 'No asset has an outstanding charge — check put-to-use dates and depreciation methods.'
            : 'No block has an opening balance, addition or deletion in this financial year.'
        }
      />
    );
  }

  const companiesAct = isCompaniesActLine(preview.lines[0]);

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: companiesAct ? 720 : 920 }}>
        <TableHead>
          {companiesAct ? (
            <TableRow>
              <TableCell sx={headerCellSx}>Asset</TableCell>
              <TableCell sx={headerCellSx}>Method</TableCell>
              <TableCell sx={headerCellSx} align="right">Opening WDV</TableCell>
              <TableCell sx={headerCellSx} align="right">Charge</TableCell>
              <TableCell sx={headerCellSx} align="right">Closing WDV</TableCell>
              <TableCell sx={headerCellSx} align="right">Days Held / Period</TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell sx={headerCellSx}>Block</TableCell>
              <TableCell sx={headerCellSx} align="right">Rate</TableCell>
              <TableCell sx={headerCellSx} align="right">Opening WDV</TableCell>
              <TableCell sx={headerCellSx} align="right">Additions (Full Rate)</TableCell>
              <TableCell sx={headerCellSx} align="right">Additions (&lt;180d, Half Rate)</TableCell>
              <TableCell sx={headerCellSx} align="right">Deletions</TableCell>
              <TableCell sx={headerCellSx} align="right">Charge</TableCell>
              <TableCell sx={headerCellSx} align="right">Closing WDV</TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {preview.lines.map((line: DepreciationLine) =>
            isCompaniesActLine(line) ? (
              <TableRow key={`asset-${line.assetId}`} sx={bodyRowSx}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.assetCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{line.assetName}</Typography>
                </TableCell>
                <TableCell>{line.method}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.openingWdv)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(line.depreciationAmount)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.closingWdv)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{line.daysHeld} / {line.daysInPeriod}</TableCell>
              </TableRow>
            ) : (
              <TableRow key={`block-${line.blockId}`} sx={bodyRowSx}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{line.blockName}</Typography>
                  {line.extinguished && (
                    <Typography variant="caption" color="error.main">
                      Block extinguished — short-term capital gain {fmtMoney(line.shortTermCapitalGain)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{line.ratePct}%</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.openingWdv)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.additionsFullRate)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.additionsHalfRate)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.deletions)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(line.depreciationAmount)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(line.closingWdv)}</TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---------------------------------------------------------------------------
// Post / Reverse confirmation dialogs — financial-close actions.
// ---------------------------------------------------------------------------

const PostRunDialog: React.FC<{
  run: DepreciationRun | null;
  onClose: () => void;
  onPosted: (message: string) => void;
}> = ({ run, onClose, onPosted }) => {
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [run]);

  if (!run) return null;

  const handlePost = async () => {
    setPosting(true);
    setError(null);
    try {
      const result = await assetService.postDepreciationRun(run.id);
      onPosted(
        result.journalEntry
          ? `Run posted — Journal Entry ${result.journalEntry.entryNumber} created`
          : 'Run posted (Income Tax memorandum book — no journal entry)'
      );
    } catch (err: any) {
      setError(err.message || 'Failed to post run');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={!!run} onClose={posting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Send color="primary" />
        Post Depreciation Run
      </DialogTitle>
      <DialogContent>
        {run.book === 'COMPANIES_ACT' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>This writes a journal entry</AlertTitle>
            Posting charges depreciation to each asset and creates a POSTED journal entry (Dr Depreciation / Cr
            Accumulated Depreciation) for {fmtMoney(run.total_amount)} across {run.asset_count} asset
            {run.asset_count === 1 ? '' : 's'}. This is not reversible from here except by explicitly reversing the run.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Memorandum only — nothing posts to the ledger</AlertTitle>
            The Income Tax book never creates a journal entry. Posting only updates each block's opening/closing WDV
            for {run.financial_year} ({fmtMoney(run.total_amount)} across {run.asset_count} block
            {run.asset_count === 1 ? '' : 's'}) for the tax return and deferred tax computation.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Period: {fmtDate(run.period_start)} – {fmtDate(run.period_end)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={posting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handlePost} disabled={posting}>
          {posting ? 'Posting…' : 'Post Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReverseRunDialog: React.FC<{
  run: DepreciationRun | null;
  onClose: () => void;
  onReversed: () => void;
}> = ({ run, onClose, onReversed }) => {
  const [reversing, setReversing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [run]);

  if (!run) return null;

  const handleReverse = async () => {
    setReversing(true);
    setError(null);
    try {
      await assetService.reverseDepreciationRun(run.id);
      onReversed();
    } catch (err: any) {
      setError(err.message || 'Failed to reverse run');
    } finally {
      setReversing(false);
    }
  };

  return (
    <Dialog open={!!run} onClose={reversing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Undo color="error" />
        Reverse Depreciation Run
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Undoes the posted charge</AlertTitle>
          {run.book === 'COMPANIES_ACT'
            ? 'Every asset in this run has its accumulated depreciation reduced back, and the linked journal entry is marked REVERSED.'
            : 'This block’s balances for the run are deleted.'}{' '}
          The period ({fmtDate(run.period_start)} – {fmtDate(run.period_end)}) becomes available for a new run.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={reversing}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleReverse} disabled={reversing}>
          {reversing ? 'Reversing…' : 'Reverse Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const DepreciationWorkbenchPage: React.FC = () => {
  const { user } = useAuth();
  const canManageRuns = Boolean(user?.role && FINANCE_ROLES.includes(user.role));

  const [book, setBook] = useState<DepreciationBook>('COMPANIES_ACT');
  const [caFrom, setCaFrom] = useState(currentMonthRange().from);
  const [caTo, setCaTo] = useState(currentMonthRange().to);
  const [itFy, setItFy] = useState(currentFinancialYear());

  const periodStart = book === 'COMPANIES_ACT' ? caFrom : financialYearDates(itFy).startISO;
  const periodEnd = book === 'COMPANIES_ACT' ? caTo : financialYearDates(itFy).endISO;

  const [preview, setPreview] = useState<DepreciationPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [runs, setRuns] = useState<DepreciationRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsBookFilter, setRunsBookFilter] = useState<DepreciationBook | ''>('');

  const [postTarget, setPostTarget] = useState<DepreciationRun | null>(null);
  const [reverseTarget, setReverseTarget] = useState<DepreciationRun | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchRuns = useCallback(() => {
    setRunsLoading(true);
    assetService
      .listDepreciationRuns({ book: runsBookFilter || undefined })
      .then((r) => setRuns(r.runs))
      .catch(() => {
        // Runs table stays empty — never fabricate history.
      })
      .finally(() => setRunsLoading(false));
  }, [runsBookFilter]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Switching book or period invalidates whatever was last previewed —
  // Create Run must always act on what's on screen right now.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    setCreateError(null);
  }, [book, periodStart, periodEnd]);

  const handlePreview = () => {
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    assetService
      .previewDepreciationRun(book, periodStart, periodEnd)
      .then(setPreview)
      .catch((err: any) => setPreviewError(err.message || 'Failed to compute preview'))
      .finally(() => setPreviewing(false));
  };

  const handleCreateRun = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const result = await assetService.createDepreciationRun(book, periodStart, periodEnd);
      setToast({ message: `Run #${result.run.id} created as DRAFT — post it below to charge depreciation.`, severity: 'success' });
      setPreview(null);
      fetchRuns();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create run');
    } finally {
      setCreating(false);
    }
  };

  const fyOptions = useMemo(() => financialYearOptions(6, 1), []);

  return (
    <Box>
      <PageHeader
        title="Depreciation Workbench"
        subtitle="Preview, create and post depreciation runs for both books"
      />

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Book
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={book}
              onChange={(_, v) => v && setBook(v)}
            >
              <ToggleButton value="COMPANIES_ACT">Companies Act</ToggleButton>
              <ToggleButton value="INCOME_TAX">Income Tax</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {book === 'COMPANIES_ACT' ? (
            <>
              <TextField
                label="Period From"
                type="date"
                size="small"
                value={caFrom}
                onChange={(e) => setCaFrom(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Period To"
                type="date"
                size="small"
                value={caTo}
                onChange={(e) => setCaTo(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </>
          ) : (
            <TextField
              select
              label="Financial Year"
              size="small"
              value={itFy}
              onChange={(e) => setItFy(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {fyOptions.map((fy) => (
                <MenuItem key={fy} value={fy}>
                  {fy}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Button variant="outlined" startIcon={previewing ? <CircularProgress size={16} /> : <PlayArrow />} onClick={handlePreview} disabled={previewing}>
            Preview
          </Button>

          {canManageRuns && (
            <Button
              variant="contained"
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddTask />}
              onClick={handleCreateRun}
              disabled={creating || !preview || preview.lines.length === 0}
            >
              Create Run
            </Button>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -0.5 }}>
          {book === 'COMPANIES_ACT'
            ? 'Period is typically one calendar month.'
            : 'Income Tax runs must span a full financial year.'}
        </Typography>

        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
          Preview is read-only and safe to run at any time — it computes lines but writes nothing. Creating a run
          saves a DRAFT; only <strong>Post</strong> (below, in Depreciation Runs) charges depreciation and, for the
          Companies Act book, writes a journal entry.
        </Alert>

        {previewError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPreviewError(null)}>
            {previewError}
          </Alert>
        )}
        {createError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
            {createError}
          </Alert>
        )}

        {previewing ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : preview ? (
          <Box>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {DEPRECIATION_BOOK_LABEL[preview.book]} · {fmtDate(preview.periodStart)} – {fmtDate(preview.periodEnd)}
              </Typography>
              <TypePill label={`${preview.assetCount} ${preview.book === 'COMPANIES_ACT' ? 'assets' : 'blocks'}`} tint="#0284c7" />
              <TypePill label={`Total ${fmtMoney(preview.totalAmount)}`} tint="#16a34a" />
            </Box>
            <PreviewTable preview={preview} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Choose a book and period, then click Preview.
          </Typography>
        )}
      </Paper>

      <FilterBar>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Depreciation Runs
        </Typography>
        <TextField
          select
          size="small"
          label="Book"
          value={runsBookFilter}
          onChange={(e) => setRunsBookFilter(e.target.value as DepreciationBook | '')}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">All Books</MenuItem>
          <MenuItem value="COMPANIES_ACT">Companies Act</MenuItem>
          <MenuItem value="INCOME_TAX">Income Tax</MenuItem>
        </TextField>
        <FilterSpacer />
        <IconButton onClick={fetchRuns} disabled={runsLoading}>
          <Refresh />
        </IconButton>
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Period</TableCell>
                <TableCell sx={headerCellSx}>Book</TableCell>
                <TableCell sx={headerCellSx}>Financial Year</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} align="right">Total Amount</TableCell>
                <TableCell sx={headerCellSx} align="right">Count</TableCell>
                <TableCell sx={headerCellSx}>Journal Entry</TableCell>
                <TableCell sx={headerCellSx} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <TableEmptyState title="No depreciation runs yet" hint="Preview a period above and create the first run." />
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((r) => (
                  <TableRow key={r.id} sx={bodyRowSx}>
                    <TableCell>{fmtDate(r.period_start)} – {fmtDate(r.period_end)}</TableCell>
                    <TableCell>{DEPRECIATION_BOOK_LABEL[r.book]}</TableCell>
                    <TableCell>{r.financial_year}</TableCell>
                    <TableCell>
                      <StatusDot label={RUN_STATUS_LABEL[r.status]} tone={RUN_STATUS_TONE[r.status]} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(r.total_amount)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum(r.asset_count)}</TableCell>
                    <TableCell>
                      {r.book === 'INCOME_TAX' ? (
                        <Typography variant="caption" color="text.secondary">Memorandum only</Typography>
                      ) : r.journal_entry_id ? (
                        <Chip size="small" icon={<Receipt sx={{ fontSize: 14 }} />} label={`JE #${r.journal_entry_id}`} variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {canManageRuns && r.status === 'DRAFT' && (
                        <Button size="small" startIcon={<Send sx={{ fontSize: 16 }} />} onClick={() => setPostTarget(r)}>
                          Post
                        </Button>
                      )}
                      {canManageRuns && r.status === 'POSTED' && (
                        <Button size="small" color="error" startIcon={<Undo sx={{ fontSize: 16 }} />} onClick={() => setReverseTarget(r)}>
                          Reverse
                        </Button>
                      )}
                      {(!canManageRuns || r.status === 'REVERSED') && (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {!canManageRuns && (
        <Alert severity="info" icon={<AccountBalance />} sx={{ mt: 2 }}>
          Creating, posting and reversing depreciation runs is restricted to Finance and Admin roles. You can still
          preview and review the run history above.
        </Alert>
      )}

      <PostRunDialog
        run={postTarget}
        onClose={() => setPostTarget(null)}
        onPosted={(message) => {
          setToast({ message, severity: 'success' });
          setPostTarget(null);
          fetchRuns();
        }}
      />
      <ReverseRunDialog
        run={reverseTarget}
        onClose={() => setReverseTarget(null)}
        onReversed={() => {
          setToast({ message: 'Run reversed', severity: 'success' });
          setReverseTarget(null);
          fetchRuns();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default DepreciationWorkbenchPage;
